
import { Component, Vue, Prop, Watch, Ref, Mixins } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";

import Sessions from "@/components/Sessions.vue";
import AreaTreeSelect from "./form/AreaTreeSelect.vue";
import ClipsList from "@/components/media/ClipsList.vue";
import AreaCameras from "@/components/media/AreaCameras.vue";
import MatrixContent from "@/components/media/MatrixContent.vue";
import Tour from "@/components/media/Tour.vue";
import AudioControl from "@/components/audio/AudioControl.vue";
import ControlDevicesList from "@/components/media/ControlDevicesList.vue";
import ControlDeviceItem from "@/components/media/ControlDeviceItem.vue";
import { ClipClipType, ClipEmitObject, defaultDeviceControllerOutput, MatrixCamera, MatrixContentItem, MatrixContents, SearchedCamera } from "@/store/site-monitor-cameras/types";

import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Multiselect from "vue-multiselect";
import VueScrollTo from "vue-scrollto";
import { CameraType, ClipType, DeviceControllerOutput, NearbyCameraType } from "@/store/site-monitor-cameras/types";
import { defaultClipsForMatrix, automationGroupKey } from "@/store/site-monitor-cameras/clipGrouping";
import { ActiveMapItem, EventDetails } from "@/store/site-monitor/types";
import { SiteTreeNode } from "@/store/eventqueue/types";
import { FeaturesList, UserPermissions } from "@/store/types";
import { get, debounce } from "lodash";

import { renderDistance } from "@/filters";
import { SessionResource } from "@/store/sessions/types";
import api from "@/services/api.service";
import RelayCapabilitiesMixin from "@/mixins/RelayCapabilitiesMixin";
import { MapLayerItemTypeIds } from '@/store/map-layers/types';
import { truncateString } from '@/filters';
import SystemViewCameraIcon from "./system-view/SystemViewCameraIcon.vue";
import vSelect from "vue-select";

const Eventqueue = namespace("eventqueue");
const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const Session = namespace("sessions");
const Tours = namespace("tours");

export interface SelectedAreaNode {
	address: string;
	cameras: any[] | null;
	children: any | null;
	id: number;
	isDefaultExpanded: boolean;
	label: string;
	latLong: string;
}

export interface SectionOption {
    id: string;
    label: string;
    enabled: boolean;
}

@Component({
	components: {
		session: Sessions,
		VuePerfectScrollbar: VuePerfectScrollbar,
		multiselect: Multiselect,
		"area-tree-select": AreaTreeSelect,
		"clips-list": ClipsList,
		"area-cameras": AreaCameras,
		tour: Tour,
		"matrix-content": MatrixContent,
		"audio-control": AudioControl,
		"control-devices-list": ControlDevicesList,
		"control-device-item": ControlDeviceItem,
		"system-view-camera-icon": SystemViewCameraIcon,
		"vue-select": vSelect
	},
	filters: {
		renderDistance,
		truncateString
	}
})
export default class DeviceController extends Mixins(RelayCapabilitiesMixin) {
	@Ref() private audioControl?: AudioControl;
	@Ref() private areaCamerasComponent?: AreaCameras;
	@Ref() private nearbyControlDevicesList?: ControlDevicesList;
	@Ref() private areaControlDevicesList?: ControlDevicesList;

	@Eventqueue.Getter("getSiteTree") tree: SiteTreeNode[];
	@Eventqueue.Mutation resetTree: any;
	@Eventqueue.Mutation setSiteTreeNodeValues: any;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getPermissions") permissions: UserPermissions;

	@Session.Action clearSessions: any;
	@Session.Action updateSession: any;

	@SiteMonitor.Getter("getActiveMapItems") mapItems: ActiveMapItem[];
	@SiteMonitor.Getter("getApplianceOfflineNotification") private applianceOfflineNotification: (deviceId: number) => string;
	@SiteMonitor.Getter("getAuditService") auditService: any;
	@SiteMonitor.Getter("getEventDetails") getEventDetails: EventDetails;
	@SiteMonitor.Getter("getIsCameraEnabled") isCameraEnabled: (deviceId: number) => boolean;
	@SiteMonitor.Getter("getIsController") isController: any;
	@SiteMonitor.Getter("getSelectedEventRecord") selectedEventRecord: any;
	@SiteMonitor.Mutation setActiveMapItemsRequired: any;

	@SMCameras.Action activeMapItemsFromStorage: any;
	@SMCameras.Action fetchAreaCameras: (params: { groupID: number, pageNumber: number, paginated: boolean }) => Promise<void>;
	@SMCameras.Action searchForCameras: (params: { query: string, groupID: number, page: number, reset: boolean }) => Promise<void>;
	@SMCameras.Action searchForOutputs: (params: { query: string, groupID: number, page: number, reset: boolean }) => Promise<void>;
	@SMCameras.Action fetchAreaOutputs: (params: { groupID: number, pageNumber: number, paginated: boolean }) => Promise<void>;

	@SMCameras.Getter getMatrixContents: (index: number) => MatrixContentItem;;
	@SMCameras.Getter searchedCameras: SearchedCamera[];
	@SMCameras.Getter searchedOutputs: DeviceControllerOutput[];
	@SMCameras.Getter("getAllMatrixContents") getAllMatrixContents: MatrixContents;
	@SMCameras.Getter("getAreaCameras") areaCameras!: CameraType[];
	@SMCameras.Getter("getAreaCameras") groupCameras: CameraType[];
	@SMCameras.Getter("getDeviceControllerCameras") cameras!: any[];
	@SMCameras.Getter("getDeviceControllerClips") clips!: ClipType[];
	@SMCameras.Getter("getDeviceControllerOutputs") outputs!: DeviceControllerOutput[];
	@SMCameras.Getter("getDeviceControllerAudioDevices") nearbyAudioDevices!: ActiveMapItem[];
	@SMCameras.Getter("getFetchingMatrix") fetchingMediaMatrix: any;
	@SMCameras.Getter("getLayoutIndex") layoutIndex!: number;
	@SMCameras.Getter("getLayouts") layouts!: number[][];
	@SMCameras.Getter("getMatrixAssignedCameras") getMatrixAssignedCameras: number[];
	@SMCameras.Getter("getMediaMatrixIsNew") mediaMatrixIsNew: boolean;
	@SMCameras.Getter("getMediaMatrixLastChanged") mediaMatrixLastChanged: any;
	@SMCameras.Getter("getMediaMatrixLayers") layers!: any[];

	@SMCameras.Mutation clearSearchedCameras: () => void;
	@SMCameras.Mutation highlightCells: (indices: number[]) => void;
	@SMCameras.Mutation setAwaitingCamera: any;
	@SMCameras.Mutation setAwaitingClip: any;
	@SMCameras.Mutation setDeviceControllerCameras: (cameras: ActiveMapItem[]) => void;
	@SMCameras.Mutation setDeviceControllerClips: (clips: any[]) => void;
	@SMCameras.Mutation setDeviceControllerOutputs: (outputs: DeviceControllerOutput[]) => void;
	@SMCameras.Mutation setDeviceControllerAudioDevices: (audioDevices: ActiveMapItem[]) => void;
	@SMCameras.Mutation setMediaMatrixEventId: any;
	@SMCameras.Mutation setMediaMatrixIsNew: any;
	@SMCameras.Mutation setPushContents: ({ index, newContents }: { index: number; newContents: MatrixContentItem; }) => void;
	@SMCameras.Mutation unhighlightCells: () => void;

	@SMCameras.State("areaCameraCount") public areaCameraCount: number;
	@SMCameras.State("areaOutputs") areaOutputs!: DeviceControllerOutput[];
	@SMCameras.State("deviceControllerAudioDevices") private audioDevices!: ActiveMapItem[];
	@SMCameras.State("searchedCameraCount") private searchedCameraCount: number;
	@SMCameras.State("searchedOutputCount") private searchedOutputCount: number;

	@Tours.Getter tourInProgress: any;

	@Prop({ default: "", type: String }) tourName;

	private cameraSearchQuery: string = "";
	private clipsQueue: ClipEmitObject[] = [];
	private debounceUpdateSearchFilter;
	private debounceUpdateOutputFilter;
	private isAreaSearch: boolean = false;
	private isLoadingAreaCameras: boolean = false;
	private isLoadingAreas: boolean = false;
	private debouncePeriod: number = 250;
	private userEnabledSections: string[] = [];

	// variable to keep track of the cell index clips are plotted in
	private lastClipIndex = 1;

	// array containing the deviceIds of cameras linked to the Alarm
	// these can then be identified in the MatrixContent component
	private linkedCameraIds: number[] = []

	// variable to hold timeout object used to populate the Media Matrix with Nearby Cameras
	private mapTimeout = null;

	// flag to track when the initial load of the Media Matrix has been completed
	private mediaMatrixInitialized = false;
	private outputSearchQuery: string = "";
	private pageNumber: number = 1;
	private populateNearbyCamerasModal: boolean = false;
	private selectedArea: SelectedAreaNode = null;

	// flat to track if the media matrix us currently being populated
	private settingMatrix: boolean = false;
	private tourEnded: boolean = false;

	// timeout seconds to wait before checking if the media matrix is ready.
	private mediaMatrixInitializationTimeout = 2000;

	private open: { [key: string]: boolean } = {
		tours: false,

		clips: true,
		
		/* Cameras Sections */
		cameras: true,
		matrixContent: true,
		nearbyCameras: true,
		searchCameras: true,

		/* Output sections */
		outputs: false,
		searchedOutputs: false,
		nearbyOutputs: false,
		areaOutputs: false,

		audio: false,

		areas: false
	};

	private isDestroyed: boolean = false;

	public collapseAllSections(): void {
		Object.keys(this.open).forEach(key => {
			this.open[key] = false;
		});

		this.audioControl?.collapseAudioSections();
		this.areaCamerasComponent?.collapseAreaCamerasSection();
		this.nearbyControlDevicesList?.collapseOutputSections();
    	this.areaControlDevicesList?.collapseOutputSections();
	}

	// have to make it initially undefined
	// to make it non-reactive to avoid additional update,
	// after changing from true to false in updated() hook.
	private scrollToAreaScheduled: boolean | undefined;

	private initialEventRecordId: number = -1;

	@Watch("tourInProgress")
	private minimizeCollapseMenu() {
		if (this.tourInProgress) {
			Object.keys(this.open).forEach(v => (this.open[v] = false));
			this.open.tours = true;
			this.open.cameras = true;
		} else {
			Object.keys(this.open).forEach(v => (this.open[v] = true));
		}
	}

	private get showAreas(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "AreaTree"]);
	}

	private get devicesEnabled(): boolean {
		return this.getFeature(["Devices"]);
	}

	private get toursEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "VideoTours"]);
	}

	private get showMatrixCameras(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "OpenCameraList"]);
	}

	private get allowSpotlightScaling(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Map", "SpotlightScaling"]);
	}

	private get showClipsList(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "ClipsList"]);
	}

	private get isDuplicateClipHandlingEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "ClipsList", "DuplicateClipHandling"]);
	}

	private get nearbyCamerasEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "NearbyCameras"]);
	}

	private get preventNearbyCamerasAutoPopulate(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "NearbyCameras", "PreventAutoPopulate"]);
	}

	private get fillMatrixNearbyCamerasEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "NearbyCameras", "FillMatrix"]);
	}

	private get fillMatrixClipsEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "ClipsList", "FillMatrixClipsButton"]);
	}

	private get fillMatrixAreaCamerasEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "AreaCameras", "FillMatrixAreaButton"]);
	}

	private get manuallyPopulateAreaCameras(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "ManuallyPopulateAreaCameras"]);
	}

	private get isAudioEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "AudioList"], false);
	}

	private get isSystemViewEnabled(): boolean {
		return this.getFeature(["SystemView"]);
	}

	private get showOptionalMediaMatrixSections(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "DefaultMediaMatrixSections"]);
	}

	private get eventId(): number {
		return this.getEventDetails ? this.getEventDetails.eventID : 0;
	}

	private get showOutputs(): boolean {
		return this.permissions.canViewMediaMatrixControls;
	}

	private get linkedCamerasEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "LinkedCameras"]);
	}

	private get shiftCamerasForClipsEnabled(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "ClipsList", "ShiftCamerasForClips"]);
	}

	private get shiftCamerasForNewAlarms(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "ClipsList", "ShiftCamerasForClips", "ShiftForAdditionalAlarms"]);
	}

	public get anySectionExpanded(): boolean {
		// Check only sections that are currently visible
		const sectionsToCheck = {
			tours: this.toursEnabled && this.shouldShowVideoTours,
			clips: this.showClipsList,
			cameras: this.devicesEnabled,
			audio: this.isAudioEnabled && this.shouldShowAudio,
			outputs: this.showOutputs && this.shouldShowOutputs,
			areas: this.showAreas && this.shouldShowAreas,
			nearbyCameras: this.nearbyCamerasEnabled,
			searchCameras: this.cameraSearchQuery && this.cameraSearchQuery.length > 0,
			matrixContent: this.showMatrixCameras,
			searchedOutputs: this.outputSearchQuery && this.outputSearchQuery.length > 0,
			nearbyOutputs: this.showOutputs && this.shouldShowOutputs && this.outputs && this.outputs.length > 0,
			areaOutputs: this.showOutputs && this.shouldShowOutputs && this.areaOutputs && this.areaOutputs.length > 0
		};

		// Check if any visible section is expanded
		return Object.keys(sectionsToCheck).some(key => {
			return sectionsToCheck[key] && this.open[key] === true;
		});
	}

	private get shouldShowAudio(): boolean {
		return this.visibleSections.includes("Audio");
	}

	private get shouldShowOutputs(): boolean {
		return this.visibleSections.includes("Controls");
	}

	private get shouldShowAreas(): boolean {
		return this.visibleSections.includes("Areas");
	}

	private get shouldShowVideoTours(): boolean {
		return this.visibleSections.includes("Video Tours");
	}

	private get defaultEnabledSections(): string[] {
		if (!this.showOptionalMediaMatrixSections) {
			return [];
		}

		const prefValue = this.getEventDetails?.defaultMediaMatrixSections || "";
		return prefValue.split(",").map(s => s.trim()).filter(Boolean);
	}

	private get allOptionalSections(): SectionOption[] {
		return [
			{ id: "Areas", label: "Areas", enabled: this.showAreas },
			{ id: "Audio", label: "Audio", enabled: this.isAudioEnabled },
			{ id: "Controls", label: "Controls", enabled: this.showOutputs },
			{ id: "Video Tours", label: "Video Tours", enabled: this.toursEnabled },
		].filter(section => section.enabled);
	}

	private get selectedSectionOptions(): SectionOption[] {
		return this.allOptionalSections.filter(section =>
			this.userEnabledSections.includes(section.id)
		);
	}

	private set selectedSectionOptions(value: SectionOption[]) {
		this.userEnabledSections = value.map(v => v.id);
	}

	// Sections available in the v-select dropdown (NOT already enabled by pref)
	private get selectableSections(): SectionOption[] {
		if (!this.showOptionalMediaMatrixSections) {
			return [];
		}

		// Only show sections that are NOT in the default pref
		return this.allOptionalSections.filter(section =>
			!this.defaultEnabledSections.includes(section.id)
		);
	}

	// All sections currently visible (default + user-selected)
	private get visibleSections(): string[] {
		if (!this.showOptionalMediaMatrixSections) {
			// Feature OFF: show all sections
			return this.allOptionalSections.map(s => s.id);
		}

		// Feature ON: combine pref defaults + user selections
		return [...this.defaultEnabledSections, ...this.userEnabledSections];
	}

	/** Get all loaded cameras within the media matrix
	 * filter to only show the objectID with the cell index.
	 */
	private get loadedCameras() {
		return Object.values(this.getAllMatrixContents)
			.filter(m => m && m.camera)
			.map(c => c.camera)
			.filter(g => g.objectID)
			.map(c => c.objectID);
	}

	private get filteredNearbyCameras(): NearbyCameraType[] {
		let camerasToFilter: NearbyCameraType[];

		// If we've got the matrix cameras section turned on, then hide any cameras
		// that we've already got open.
		camerasToFilter = this.cameras;

		if (this.cameraSearchQuery === "") {
			return camerasToFilter;
		}

		const filterValue = this.cameraSearchQuery.toLocaleLowerCase();
		return camerasToFilter.filter(camera => camera.title.toLocaleLowerCase().indexOf(filterValue) > -1);
	}

	@Watch("shouldShowOutputs")
	private async onShouldShowOutputsChanged(value: boolean, oldValue: boolean): Promise<void> {
		// Only fetch when section is being enabled (false → true)
		if (value && !oldValue && this.showOutputs) {
			// Refresh nearby outputs from map items
			if (this.mapItems && this.mapItems.length > 0) {
				await this.setNearByOutputs(this.mapItems);
			}

			// Fetch area outputs from API
			if (this.getEventDetails) {
				await this.fetchAreaOutputs({
					groupID: this.getEventDetails.groupID,
					pageNumber: 1,
					paginated: true
				});
			}
		}
	}

	@Watch("eventId")
	private onEventChanged(eventId: number, oldEventId: number) {
		this.lastClipIndex = 1;
		this.resetTree();
		this.resetCameraState();

		this.setAwaitingCamera(null);

		if (eventId != 0 && this.showAreas && this.shouldShowAreas && this.shouldShowAreas && this.open.areas && this.getEventDetails && this.tree) {
			this.openArea(this.getEventDetails.groupID);
		}

		this.open.cameras = true;
		this.open.clips = true;
	}

	private async resetCameraState(): Promise<void> {
		if (!this.shouldShowAreas) {
			return;
		}

		this.cameraSearchQuery = "";
		this.pageNumber = 1;
		if (this.getEventDetails) {
			await this.fetchAreaCameras({ groupID: this.getEventDetails.groupID, pageNumber: this.pageNumber, paginated: true });
		}
	}

	private checkCamerasComparison(newData: any, oldData: any): boolean {
		return newData.count == oldData.count && JSON.stringify(newData) !== JSON.stringify(oldData);
	}

	private get totalCells() {
		return this.layouts[this.layoutIndex][0] * this.layouts[this.layoutIndex][1];
	}

	private async loadAlarmEvent(newData: any, oldData: any, dataChanged: boolean) {
		if (!dataChanged || this.tourInProgress || !this.allowSpotlightScaling || !this.getEventDetails) {
			return;
		}

		this.populateNearbyCamerasModal = true;
	}

	@Watch("cameras")
	private async loadNearbyCameraLayer(newData: any, oldData: any) {
		/** Check to see if any of the new or old data incoming is invalid for this type of operation. */
		if (!newData || newData.length <= 0 || !oldData || oldData.length <= 0) return;

		if (this.getEventDetails && !this.getEventDetails.useNearbyCameras) return;

		// Event Types 'EventTypeID'
		const alarmEvent = 1;
		const patrolEvent = 2;

		const dataChanged = this.checkCamerasComparison(newData, oldData);
		if (this.getEventDetails.eventTypeID == alarmEvent) {
			this.loadAlarmEvent(newData, oldData, dataChanged);
		} else if (this.getEventDetails.eventTypeID == patrolEvent) {
			await this.loadNearbyCameras();
		}
	}

	@Watch("areaCameras")
	public async loadAreaCameras(newData: any, oldData: any) {
		/**
		 * Note - this function has been added to populate the media matrix with area cameras
		 * This will likely be turned off when nearby cameras are set up in ops.
		 */
		// if nearby cameras are enabled, don't use area cameras and add check if we have cameras to add
		if (this.nearbyCamerasEnabled || !newData || newData.length <= 0) return;

		// populate the media matrix with area cameras
		await this.loadCameras(this.areaCameras);
	}

	// Watch the pageNumber and reuse the same search query to update list
	@Watch("pageNumber")
	public async onPageNumberChanged(): Promise<void> {
		if (!this.cameraSearchQuery && !this.outputSearchQuery && !this.isAreaSearch) {
			this.pageNumber = 1;
			this.clearSearchedCameras();
		} else if (this.outputSearchQuery && !this.isAreaSearch) {
			await this.findOutputs(this.outputSearchQuery, false);
		} else if (this.cameraSearchQuery && !this.isAreaSearch) {
			await this.findCameras(this.cameraSearchQuery, false);
		} else {
			await this.areaSelected(this.selectedArea);
		}
	}

	@Watch("cameraSearchQuery")
	public async onCameraSearchQueryChanged(value: string) {
		this.pageNumber = 1;
		this.debounceUpdateSearchFilter(value);
	}

	@Watch("outputSearchQuery")
	public onOutputSearchQueryChanged(value: string) {
		if (this.shouldShowOutputs) {
			this.debounceUpdateOutputFilter(value);
		}
	}

	@Watch("mediaMatrixLastChanged")
	public onMatrixChanged(value: any) {
		if (this.searchedCameras != null && this.searchedCameras.length > 0) {
			this.setSearchCameraIndexes();
		}
	}

	@Watch("mapItems")
	public async onMapItemsChanged(value: any): Promise<void> {
		// clear out timeout to avoid memory leaks
		this.clearMapTimeout();

		if (!this.getEventDetails) {
			return;
		}
	
		if (!this.mediaMatrixInitialized) {
			// set new timeout to check status again in 2 seconds
			if (this.mapTimeout != null) {
				this.clearMapTimeout();
			}
			
			if (!this.isDestroyed) {
				this.mapTimeout = setTimeout(() => this.onMapItemsChanged(value), this.mediaMatrixInitializationTimeout);
				return;
			}
		}

		// we have map items
		if (value && value.length > 0) {
			await this.setDevicesFromMapLayerItems(value);
			return;
		}

		// we have no map items, so we need to populated the area cameras (after clips)
		this.populateAreaCameras([], this.lastClipIndex);
	}

	@Watch("areaOutputs")
	private onAreaOutputsChanges(value: DeviceControllerOutput, oldValue: DeviceControllerOutput): void {
		// Nearby uses area Outputs data so ensure up to date.
		// Only update when section is visible.
		if (this.shouldShowOutputs) {
			this.setNearByOutputs(this.mapItems);
		}
	}

	public beforeDestroy(): void {
		this.isDestroyed = true;
		this.clearMapTimeout();
	}

	private clearMapTimeout(): void {
		if (this.mapTimeout != null) {
			clearTimeout(this.mapTimeout);
			this.mapTimeout = null;
		}
	}

	private cancelCameraSelection(): void {
		this.setAwaitingCamera(null);
		this.$emit('cancel-camera-selection');
	}

	private cancelClipSelection(): void {
		this.setAwaitingClip(null);
		this.$emit('cancel-clip-selection');
	}

	public async mounted() {
		this.setActiveMapItemsRequired(true);

		if (this.getEventDetails && this.showOutputs && this.shouldShowOutputs) {
			await this.fetchAreaOutputs({
				groupID: this.getEventDetails.groupID,
				pageNumber: 1,
				paginated: true
			});
		}

		this.debounceUpdateSearchFilter = debounce(async (value) => {
			await this.findCameras(value, true);
		}, this.debouncePeriod);
		this.debounceUpdateOutputFilter = debounce(async (value) => {
			await this.findOutputs(value, true);
		}, this.debouncePeriod);
	}

	public updated() {
		if (this.scrollToAreaScheduled) {
			var element = document.getElementById("TreeGroup_" + this.getEventDetails.groupID);
			VueScrollTo.scrollTo(element, 50, {
				container: "#collapse-areas"
			});
			this.scrollToAreaScheduled = false;
		}
	}

	public async loadNearbyCameras() {
		await this.loadCameras(this.cameras);
		this.populateNearbyCamerasModal = false;
	}

	private async loadCameras(cameras: any[]) {
		let camerasToPush = [];
		const attachedCameras: any[] = this.selectedEventRecord?.attachedCameras.filter(ac => !this.getMatrixAssignedCameras.includes(this.GetCameraId(ac)));
		if (attachedCameras && attachedCameras.length > 0) {
			const attached = attachedCameras
				.map(c => ({ objectID: c.deviceID, title: c.title }));
			const notAttached = cameras.filter(c => !attached.find(ac => ac.objectID === this.GetCameraId(c)));
			camerasToPush = [...attached, ...notAttached]
		} else {
			camerasToPush = cameras.filter(ac => !this.getMatrixAssignedCameras.includes(this.GetCameraId(ac)));
		}
		// add a camera to every cell (as long as we have enough cameras)

		if (camerasToPush && camerasToPush.length) {
			this.setMatrixCells(camerasToPush);
		}

	}

	public created() {
		this.updateSession({ resourceId: SessionResource.DeviceServiceSession, eventId: this.eventId });
		this.setMediaMatrixEventId(this.eventId);
	}

	public destroyed(): void {
		this.clearMapTimeout();
		this.linkedCameraIds = [];
		this.mediaMatrixInitialized = false;
		this.lastClipIndex = 1;
		this.settingMatrix = false;
		this.setMediaMatrixIsNew(true);
	}

	public async setDevicesFromMapLayerItems(devices: ActiveMapItem[]): Promise<void> {
		if (!devices) {
			devices = [];
		}

		const cameras = devices.filter(device => device.itemType === MapLayerItemTypeIds.Camera);

		const audios = devices.filter(device => device.itemType === MapLayerItemTypeIds.Audio);

		// Only set nearby outputs/devices if the section is visible
		if (this.shouldShowOutputs) {
			await this.setNearByOutputs(devices);
		}

		this.setDeviceControllerCameras(cameras);

		this.setDeviceControllerAudioDevices(audios);

		let cellIndex = this.lastClipIndex;
		let nearbyCameras = [];
		if (this.nearbyCamerasEnabled && !this.preventNearbyCamerasAutoPopulate) {
			nearbyCameras = this.cameras ? this.cameras.filter((c: any) => !this.getMatrixAssignedCameras.includes(this.GetCameraId(c))) : [];
			if (nearbyCameras && nearbyCameras.length > 0) {
				cellIndex = this.setMatrixCells(nearbyCameras, cellIndex);
			}
		}

		this.populateAreaCameras(nearbyCameras,cellIndex);
	}

	private populateAreaCameras(nearbyCameras: any[] = [], cellIndex:number = 1): void {
		// Set area cameras
		if (!this.manuallyPopulateAreaCameras && this.areaCameras && this.areaCameras.length > 0) {
			const newAreaCameras = this.areaCameras.filter(c => !this.getMatrixAssignedCameras.includes(this.GetCameraId(c)));

			// filter the area cameras to exclude any cameras that were already plotted as a nearby camera
			// if a camera is slow or fails to load, getMatrixAssignedCameras will not contain the objectId for that camera
			const newAreaCamerasExcludingNearby = newAreaCameras.filter(c => !nearbyCameras.map(m => m.objectId).includes(this.GetCameraId(c)));
			if (newAreaCamerasExcludingNearby && newAreaCamerasExcludingNearby.length > 0) {
				this.setMatrixCells(newAreaCamerasExcludingNearby, cellIndex);
			}
		}
	}

	private async setNearByOutputs(devices: any[]): Promise<void> {
		// We want Outputs/Devices not MapLayerItems
		const outputs: DeviceControllerOutput[] = await this.getOutputsFromMapLayerItems(devices);
		this.setDeviceControllerOutputs(outputs);
	}

	private async getOutputsFromMapLayerItems(devicesAsMapLayerItems: any[]): Promise<DeviceControllerOutput[]> {
		devicesAsMapLayerItems = devicesAsMapLayerItems
			.filter(device =>
				device.itemType === MapLayerItemTypeIds.Relay ||
				device.itemType === MapLayerItemTypeIds.Output);

		let deviceIds: number[] = [];
		let outputs: DeviceControllerOutput[] = [];

		// We need to convert MapLayerItems to devices
		devicesAsMapLayerItems.forEach(device => {
			const result = this.areaOutputs.firstOrDefault(o => o.objectId == device.objectId);

			// Any devices (MapLayerItems) we don't have locally will have to be requested.
			if (!result) {
				deviceIds.push(device.objectId);
				const output = {
					...defaultDeviceControllerOutput(),
					... {
						title: device.title,
						distance: device.distance,
						objectId: device.objectId
					}
				};
				outputs.push(output);
			} else {
				const newOutput = { ...result };
				newOutput.distance = device.distance;
				outputs.push(newOutput);
			}
		});

		// Request device data for missing MapLayerItems
		if (deviceIds.length > 0) {
			const missingOutputs = await api.getDevicesByIds(deviceIds);

			// Update devices with missing data.
			missingOutputs.forEach(mOutput => {
				var index = outputs.findIndex(output => output.objectId === mOutput.deviceID);
				if (index > -1) {
					const { isOutput, canPulse, canOnOff } = this.getRelayCapabilities(mOutput.settingsMeta);
					outputs[index].isOutput = isOutput;
					outputs[index].canPulse = canPulse;
					outputs[index].canOnOff = canOnOff;
				}
			});
		}

		return outputs;
	}

	private get AreaCamerasNotAssignedInMatrix(): CameraType[] {
		return this.areaCameras ? this.areaCameras.filter((c: any) => !this.getMatrixAssignedCameras.includes(this.GetCameraId(c))) : [];
	}

	// Clips eligible to fill the matrix: with duplicate grouping on, the "best" clip of each
	// record automation that has nothing on the matrix yet (FR-003b) — a group the operator has
	// already placed a clip from is left alone, so filling never puts a second clip from the same
	// automation up. Otherwise every clip (FR-010).
	private get clipsForMatrixFill(): ClipType[] {
		return this.isDuplicateClipHandlingEnabled ? this.bestClipsAmong(this.clips) : this.clips;
	}

	// The subset of the given clips that may be auto-placed: the current "best" of their record
	// automation AND belonging to a group that has no clip on the matrix yet. This keeps
	// auto-population to one clip per group even as clips arrive/convert one at a time.
	private bestClipsAmong(clips: ClipType[]): ClipType[] {
		const groupKeyOf = (c: ClipType) => automationGroupKey(c) || `solo:${c.UniqueFileIdentifier}`;
		const bestIds = new Set(defaultClipsForMatrix(this.clips).map((c) => c.UniqueFileIdentifier));
		const onMatrix = this.getMatrixClipsSet();
		const occupiedGroups = new Set(
			this.clips.filter((c) => onMatrix.has(c.UniqueFileIdentifier)).map(groupKeyOf)
		);
		return (clips || []).filter(
			(c) => bestIds.has(c.UniqueFileIdentifier) && !occupiedGroups.has(groupKeyOf(c))
		);
	}

	private get ClipsFillButtonDisabled(): boolean {
		return this.settingMatrix || this.clips.length == 0 || this.unAssignedMatrixClips(this.clipsForMatrixFill).length == 0;
	}

	private get AreaFillButtonDisabled(): boolean {
		return this.settingMatrix || this.areaCameras.length == 0 || this.AreaCamerasNotAssignedInMatrix.length == 0;
	}

	private get FillButtonDisabled(): boolean {
		return this.settingMatrix || this.filteredNearbyCameras.length == 0 || this.NearbyCamerasNotAssignedInMatrix.length == 0;
	}

	private get NearbyCamerasNotAssignedInMatrix(): any[] {
		return this.filteredNearbyCameras ? this.filteredNearbyCameras.filter((c: any) => !this.getMatrixAssignedCameras.includes(this.GetCameraId(c))) : [];
	}

	private fillEmptyCellsWithClips(): void {

		const clips = this.unAssignedMatrixClips(this.clipsForMatrixFill);
		if (!clips || clips.length <= 0) {
			return;
		}
		this.setMatrixCells(clips);
	}

	private fillEmptyCellsWithNearbyCameras(): void {
		const nearbyCameras = this.NearbyCamerasNotAssignedInMatrix;
		if (!nearbyCameras || nearbyCameras.length <= 0) {
			return;
		}
		nearbyCameras.sort((a, b) => (a.distance as number) - (b.distance as number));
		this.setMatrixCells(nearbyCameras);
	}

	private getMatrixClipsSet(): Set<string> {
		const matrixClips = new Set<string>();
		const matrixContents = this.getAllMatrixContents;

		// iterate over current cells in the matrix
		for (var matrixIndex in matrixContents) {
			if (matrixContents[matrixIndex].clip !== undefined) {
				// get the cells clip
				let clip = matrixContents[matrixIndex].clip.clip;
				if (clip != null) {
					// add index value to the array under the clips unique file identifier
					let clipIdentifier = clip.UniqueFileIdentifier;
					matrixClips.add(clipIdentifier);
				}
			}
		}

		return matrixClips;
	}

	private clipsRetrieved(clips: ClipEmitObject) {
		if (this.fetchingMediaMatrix) {
			this.clipsQueue.push(clips);
		} else {
			this.fillIfNew(clips);
		}
	}

	@Watch("fetchingMediaMatrix")
	private fetchingMediaMatrixChanged(value: boolean, oldValue: boolean) {
		if (!value) {
			while (this.clipsQueue.length > 0) {
				this.fillIfNew(this.clipsQueue.shift());
			}
		}
	}

	private fillIfNew(payload: ClipEmitObject) {
		this.initialEventRecordId = payload.eventRecordID;
		let index = 1;
		if (this.mediaMatrixIsNew) {
			this.setMediaMatrixIsNew(false);

			// Set clips - CRITICAL, This has to be done before loading the live streams, otherwise the clips will never populate if there
			//	are 9 or more nearby cameras for the alarm
			if (this.clips && this.clips.length > 0) {
				// Duplicate-clip grouping: on first load, place only one "best" clip per record
				// automation (FR-003). When the feature is off, place every clip as today (FR-010).
				const clipsToPlace = this.isDuplicateClipHandlingEnabled
					? defaultClipsForMatrix(this.clips)
					: this.clips;
				this.lastClipIndex = clipsToPlace.length + 1;
				index = this.setMatrixCells(clipsToPlace, index);
			}

			if (this.linkedCamerasEnabled) {
				//Set event record linked cameras
				payload.cameras = payload.cameras?.filter((cam, idx, arr) => arr.findIndex(c => this.GetCameraId(c) === this.GetCameraId(cam)) === idx //distinct
																				&& !this.getMatrixAssignedCameras.includes(this.GetCameraId(cam))); // not already in AssignedCameras
				if (payload.cameras && payload.cameras.length > 0) {

					// track the ids of the linked cameras so we match them up to the cameras populated in the media matrix
					this.linkedCameraIds = payload.cameras.map(cam => cam.deviceID);
					index = this.setMatrixCells(payload.cameras, index);
				}
			}

			this.mediaMatrixInitialized = true;
		} else {
			// Matrix was restored from storage
			if (!this.mediaMatrixInitialized) {
				this.mediaMatrixInitialized = true;
			}

			if (this.getEventDetails && this.mediaMatrixInitialized) {
				// new clips have been received
				if (payload.newClips && payload.newClips.length > 0) {
					// With duplicate grouping on, only auto-place the "best" clip of each record
					// automation — never auto-add duplicates (those are added manually, FR-003).
					// Feature off: auto-place every new clip as today (FR-010).
					const newClips = this.isDuplicateClipHandlingEnabled
						? this.bestClipsAmong(payload.newClips)
						: payload.newClips;
					// clips that are not already assigned in the Media Matrix
					const filtered = this.unAssignedMatrixClips(newClips);

					if (filtered && filtered.length > 0) {
						// if feature is on, new clips will replace camera cells
						if (this.shiftCamerasForClipsEnabled) {
							// if the clip is from the initial event record (slow loading)
							// or the shiftCamerasForNewAlarms is set to true
							if (filtered[0]?.EventRecordId == this.initialEventRecordId || this.shiftCamerasForNewAlarms) {
								if (filtered && filtered.length > 0) {
									// get existing Media Matrix contents
									const matrix = Object.entries(this.getAllMatrixContents);

									index = this.setMatrixCells(filtered, this.lastClipIndex, true);

									let existingCameras = [];

									// pull out any existing cameras so we can re plot them after the new clips
									matrix.forEach(cell => {
										if (cell[1].camera) {

											const camera: MatrixCamera = {
												title: cell[1].camera.title,
												objectID: cell[1].camera.objectID,
												index: null
											}

											existingCameras.push(camera);
										}
									});

									if (existingCameras.length > 0) {
										// shift cameras so new clip can be seen
										index = this.setMatrixCells(existingCameras, index, true);
									}
								}
							}
						}
						else {
							// plot new clips
							index = this.setMatrixCells(filtered);
						}
					}
				}
			}
		}
	}

	private unAssignedMatrixClips(clips: any[]): any[] {
		const matrixClips = this.getMatrixClipsSet();

		let filtered = [];
		clips.forEach(clip => {
			if (!matrixClips.has(clip.UniqueFileIdentifier)) {
				filtered.push(clip);
			}
		});

		return filtered;
	}

	public cameraSelect(camera: CameraType | NearbyCameraType | SearchedCamera): void {
		if (this.isController) {
			this.setAwaitingCamera(camera);
		}
	}

	public clipSelect(clip: any) {
		if (this.isController && clip?.EventRecordId) {
			this.setAwaitingClip({
				eventRecordID: clip.EventRecordId,
				clip: clip
			});
		}
	}

	public get layoutHorizontal() {
		return this.layouts[this.layoutIndex][0];
	}

	public get layoutVertical() {
		return this.layouts[this.layoutIndex][1];
	}

	public cameraIconColor(camera: any, i: number, j: number) {
		return (i - 1) * this.layoutHorizontal + j === camera.index ? "redCameraIndicator" : "greyCameraIndicator";
	}

	public isSelectedCamera(camera: NearbyCameraType | SearchedCamera):string {
		return camera.index !== null && !isNaN(camera.index) ? "selectedCamera" : "";
	}

	private async findCameras(query: any, reset: boolean) {
		let groupID = this.getEventDetails.groupID;
		let page = this.pageNumber;
		await this.searchForCameras({ query, groupID, page, reset });
		this.setSearchCameraIndexes();
	}

	private async findOutputs(query: string, reset: boolean) {
		if (!this.shouldShowOutputs) {
			return;
		}

		const groupID = this.getEventDetails.groupID;
		const page = this.pageNumber;
		await this.searchForOutputs({ query, groupID, page, reset });
	}

	private setSearchCameraIndexes() {
		let deviceIndexes = {};
		let maxtrixContents = this.getAllMatrixContents;

		for (var matrixIndex in maxtrixContents) {
			if (maxtrixContents[matrixIndex] != null && maxtrixContents[matrixIndex].camera != null) {
				deviceIndexes[maxtrixContents[matrixIndex].camera.objectID] = parseInt(matrixIndex);
			}
		}

		this.searchedCameras.forEach(camera => {
			camera.index = deviceIndexes[camera.objectID] != null ? deviceIndexes[camera.objectID] : null;
		});
	}

	public async openArea(groupID: number) {
		const openIfMatch = async (node: SiteTreeNode, groupID: number) => {
			if (node.group.groupID == groupID) {
				if (node.isOpen || !node.group.selected) {
					this.setSiteTreeNodeValues({
						treeNode: node,
						isOpen: false,
						selected: true
					});
				}

				if (node.subGroups && node.subGroups.length > 0) {
					const loadPromises = node.subGroups.map(sn => openIfMatch(sn, groupID));
					await Promise.all(loadPromises);
				}

				if (!node.loadingContents && node.contents == null) {
					this.setSiteTreeNodeValues({
						treeNode: node,
						loadingContents: true,
						contentsOpen: true
					});

					await this.fetchAreaCameras({ groupID: node.group.groupID, pageNumber: this.pageNumber, paginated: true });

					this.setSiteTreeNodeValues({
						treeNode: node,
						contents: {
							cameras: this.areaCameras
						},
						loadingContents: false
					});
				}

				return true;
			} else {
				let matchesChild: boolean = false;

				if (node.subGroups && node.subGroups.length > 0) {
					node.subGroups.forEach(async (subnode: SiteTreeNode) => {
						let match = await openIfMatch(subnode, groupID);
						if (match) {
							matchesChild = match;
						}
					});
				}

				if (node.isOpen != matchesChild || node.group.selected) {
					this.setSiteTreeNodeValues({
						treeNode: node,
						isOpen: matchesChild,
						selected: false
					});
				}

				return matchesChild;
			}
		};

		const openPromises = this.tree.map(n => openIfMatch(n, groupID));
		await Promise.all(openPromises);

		this.scrollToAreaScheduled = true;
	}

	public async areaSelected(selectedAreaNode: SelectedAreaNode): Promise<void> {
		if (!this.shouldShowAreas) {
			return;
		}

		this.selectedArea = selectedAreaNode;

		if (selectedAreaNode && selectedAreaNode.id) {

			this.isLoadingAreaCameras = true;

			await this.fetchAreaCameras({ groupID: selectedAreaNode.id, pageNumber: this.pageNumber, paginated: true });

			this.selectedArea.cameras = this.areaCameras;

			this.isLoadingAreaCameras = false;
		} else {
			await this.resetCameraState();
		}
	}

	public showAreaCamera(camera: any, treeSelectCamera: boolean): void {
		if (this.isController) {
			this.setAwaitingCamera({
				objectID: treeSelectCamera ? camera.objectID : camera.deviceID,
				itemType: 1,
				title: camera.title
			});
		}
	}

	private setTourName(data: any) {
		this.tourName = data;
	}

	private async toggleAreasSection() {
		this.open.areas = !this.open.areas;
		if (this.open.areas && this.shouldShowAreas) {
			try {
				this.isLoadingAreas = true;
				await this.openArea(this.getEventDetails.groupID);
				this.isLoadingAreas = false;
			}
			catch (ex) {
				console.log("Matrix: DeviceController - Unexpected error loading site tree: " + ex);
			}
			finally {
				this.isLoadingAreas = false;
			}
		}
	}

	// Increasing the pageNumber will trigger the watcher on pageNumber
	private showMoreSearchCameras(): void {
		this.isAreaSearch = false;
		this.pageNumber++;
	}

	private showMoreAreaCameras(): void {
		this.isAreaSearch = true;
		this.pageNumber++;
	}

	private showMoreOutputs(): void {
		this.isAreaSearch = false;
		this.pageNumber++;
	}

	private resetCameraPageNumber(): void {
		this.tourEnded = true;
	}

	private completeCameraReset(): void {
		this.tourEnded = false;
	}

	private get showSearchLoadMoreButton(): boolean {
		return this.searchedCameras.length > 0 && this.searchedCameras.length < this.searchedCameraCount;
	}

	private get showOutputsLoadMoreButton(): boolean {
		return this.searchedOutputs && this.searchedOutputs.length > 0 && this.searchedOutputs.length < this.searchedOutputCount;
	}

	private get showAreaLoadMoreButton(): boolean {
		return this.areaCameras.length > 0 && this.areaCameras.length < this.areaCameraCount;
	}

	/**
	 * Finds and fills empty cells in the Media Matrix with items from the provided array
	 *
	 * @param {any[]} items Array of cameras or Clips to plot in the Media Matrix.
	 * @param {number} startingIndex index of matrix cells to start processing
	 * @param {boolean} overWriteCells flag to state if cell content should be overwritten
	 */
	private setMatrixCells(items: any[], startingIndex: number = 1, overWriteCells: boolean = false): number {
		// null or empty array then return
		if (!items || items.length == 0) {
			return
		}

		// if we are not plotting cells
		if (!this.settingMatrix) {
			this.settingMatrix = true;

			// get size of media matrix
			const cellCount = this.layoutHorizontal * this.layoutVertical;
			let addedItemIndex = 0;
			let index = startingIndex && startingIndex > 0 ? startingIndex : 1;

			// iterate over each cell to determine if we can add any content to it
			while (index <= cellCount) {
				try {
					// exit if we have processed all previous items
					// !!! IMPORTANT - do not move this line down or its possible to get stuck in a while loop
					if (addedItemIndex >= (items.length || cellCount) || index > cellCount) {
						break;
					}

					// get the item from the next item from the list
					const item = items[addedItemIndex];

					// skip if item is invalid or already has an index (already plotted)
					if (!item || item.index != null || item?.clip?.index != null) {
						addedItemIndex++;
						continue;
					}

					// get current context from the cell based on its index
					const matrixContent = this.getAllMatrixContents;
					const cellContents = matrixContent[index];

					// Skip to the next cell if it has content
					if (!overWriteCells && cellContents != null && (cellContents.clip || cellContents?.clip?.clip || cellContents.camera?.objectID)) {
						index++;
						continue;
					}

					let newClipItem: ClipClipType = undefined;
					let newCameraItem: MatrixCamera = undefined;
					let isClip = false;

					// work out if item is a clip or camera
					if (item.hasOwnProperty('UniqueFileIdentifier')) {
						isClip = true;
						newClipItem = {
							clip: {
								active: undefined,
								CreatedGroupTimezone: item.CreatedGroupTimezone,
								CreatedUTC: item.CreatedUTC,
								DeviceId: item.DeviceId,
								DeviceTitle: item.DeviceTitle,
								Error: item.Error,
								EventRecordId: item.EventRecordId,
								FileIdentifier: item.FileIdentifier,
								FileSize: item.FileSize,
								FileSizeError: item.FileSizeError,
								FriendlyStartTime: item.FriendlyStartTime,
								index: index,
								MediaType: item.MediaType,
								StartTime: item.StartTime,
								Subtitle: item.Subtitle,
								title: undefined,
								UniqueFileIdentifier: item.UniqueFileIdentifier
							},
							eventRecordID: item.EventRecordId
						}
					}
					else {
						// cast the incoming item
						newCameraItem = {
							index: index,
							objectID: this.GetCameraId(item),
							title: item.title
						};
				}

					if (!newCameraItem && !newClipItem) {
						// item is already plotted to skip it
						addedItemIndex++;
						continue;
					}

					let newCellContent: MatrixContentItem = {};

					if (isClip) {
						newCellContent.clip = newClipItem;
					}
					else {
						newCellContent.camera = newCameraItem;
					}

					this.setPushContents({ index: index, newContents: newCellContent });

					index++;
					addedItemIndex++;

				} catch (error) {
					console.error("Matrix: DeviceController - Failed to add item to Media Matrix", error);
					addedItemIndex++;

					// fallback encase we end up in a try catch loop
					if (addedItemIndex >= (items.length || cellCount) || index > cellCount) {
						this.settingMatrix = false;
						break;
					}
				}
			}
			this.settingMatrix = false;
			return index;
		}
	}

	private GetCameraId(item: any) {
		// default to -1 (not set)
		let cameraId = -1;

		if (item.hasOwnProperty('deviceId')) {
			cameraId = item.deviceId;
		}
		if (item.hasOwnProperty('deviceID')) {
			cameraId = item.deviceID;
		}
		if (item.hasOwnProperty('objectID')) {
			cameraId = item.objectID;
		}
		if (item.hasOwnProperty('objectId')) {
			cameraId = item.objectId;
		}

		return cameraId
	}

	private getSectionIcon(sectionId: string): string {
        const iconMap: { [key: string]: string } = {
            'Video Tours': 'far fa-play-circle',
            'clips': 'far fa-film',
            'cameras': 'far fa-cctv',
            'Audio': 'fas fa-speaker',
            'Controls': 'far fa-door-open',
            'Areas': 'far fa-city'
        };

        return iconMap[sectionId] || '';
    }
}
